import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from '../config'

import {
  MdDashboard,
  MdDevices
} from "react-icons/md";
import { FaUsers, FaBuilding, FaHandshake, FaHome, FaBell, FaWrench, FaEllipsisH, FaClipboardList, FaUserPlus } from "react-icons/fa";
import { FiLogIn } from "react-icons/fi";

import EntryExitLog from "../components/EntryExitLog";
import Dashboard from "../components/Dashboard";
import SmartDevices from "../components/SmartDevices";
import { AddDeviceModal } from "../components/AddDeviceModal";
import VisitorManagement from "../components/VisitorManagement";
import AddVisitorModal from "../components/AddVisitorModal";
import { registerPushNotifications, unregisterPushNotifications } from "../pushNotifications";
import {
  getNotifications,
  subscribeNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  getUnreadCount,
  onNotificationTap,
} from "../pushNotifications";
import BroadcastNotification from "../components/BroadcastNotification";
import NotificationPanel from "../components/NotificationPanel";
import ProfileMenu from "../components/ProfileMenu";
import PartnersManagement from "../components/PartnersManagement";
import AdminIssueManagement from "../components/AdminIssueManagement";
import AdminBookingManagement from "../components/AdminBookingManagement";
import AdminAmenitiesManagement from "../components/AdminAmenitiesManagement";
import AdminVacateManagement from "../components/AdminVacateManagement";
import ResidentManagement from "../components/ResidentManagement";
import TrialGate from "../components/TrialGate";
import TrialBanner from "../components/TrialBanner";
import CommunityGateConfig from "../components/CommunityGateConfig";
import { getCommunity, submitUpgradeRequest } from "../services/onboardingService";
import { getPortalRoute, normalizeRole } from "../utils/roleRouting";

export default function ManagementPortal() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [clubhouseSubTab, setClubhouseSubTab] = useState("bookings");
  const [featureFlags, setFeatureFlags] = useState(null);
  const [communityPlan, setCommunityPlan] = useState('');
  const [isAddVisitorOpen, setIsAddVisitorOpen] = useState(false);
  const [visitorFilter, setVisitorFilter] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState(() => getNotifications());
  const unreadCount = notifications.filter((n) => !n.read).length;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  const [isAdmin, setIsAdmin] = useState(() => (sessionStorage.getItem('user_role') || '').toLowerCase() === 'admin');
  const communityId = sessionStorage.getItem('community_id') || '';
  const isTrial = communityPlan === '' ? undefined : communityPlan === 'TRIAL';

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: MdDashboard },
    { id: "visitors", label: "Visitors", icon: FaUsers },
    { id: "entry-exit", label: "Entry/Exit", icon: FiLogIn },
    { id: "issues", label: "Issues", icon: FaWrench },
    { id: "devices", label: "Devices", icon: MdDevices },
    ...(isAdmin ? [{ id: "notifications", label: "Notify", icon: FaBell }] : []),
    { id: "clubhouse", label: "Clubhouse", icon: FaBuilding },
    { id: "partners", label: "Partners", icon: FaHandshake },
    ...(isAdmin ? [{ id: "vacate-requests", label: "Vacate Requests", icon: FaClipboardList }] : []),
    ...(isAdmin ? [{ id: "residents", label: "Residents", icon: FaUserPlus }] : []),
    { id: "properties", label: "Properties", icon: FaHome }
  ];

  // Ensure the signed-in user is registered in REG_COMM_USER table.
  useEffect(() => {
    (async () => {
      try {
        const idToken = sessionStorage.getItem('id_token')
        if (!idToken) {
          // No token -> redirect to login
          navigate('/')
          return
        }

        // decode email claim from id_token
        let email = ''
        try {
          const payload = JSON.parse(decodeURIComponent(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join('')))
          email = (payload.email || '').toString().toLowerCase()
        } catch (err) {
          console.error('failed to parse id_token in ManagementPortal', err)
        }

        if (!email) {
          sessionStorage.removeItem('id_token')
          navigate('/access-denied')
          return
        }

        const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
        const resp = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } })
        if (!resp.ok) {
          sessionStorage.removeItem('id_token')
          navigate('/access-denied')
          return
        }
        let data = await resp.json()
        const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || '').toString().toLowerCase()
        if (returned !== email) {
          sessionStorage.removeItem('id_token')
          navigate('/access-denied')
          return
        }
        // Multi-community: resolve the record that was selected in the community picker
        if (data.multiCommunity && Array.isArray(data.communities)) {
          const selectedCid = sessionStorage.getItem('community_id') || ''
          const selectedUid = sessionStorage.getItem('user_id') || ''
          const match = data.communities.find(
            c => {
              const cid = c.COMMUNITY_ID || c.communityId || ''
              const uid = c.USER_ID || c.userId || ''
              return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid)
            }
          ) || data.communities[0]
          if (!match) { navigate('/access-denied'); return }
          data = match
        }
        // Registered — store communityId for downstream components
        const cid = data.COMMUNITY_ID || data.communityId || data.community_id || ''
        if (cid) sessionStorage.setItem('community_id', cid)
        const cname = data.COMMUNITY_NAME || data.communityName || ''
        if (cname) sessionStorage.setItem('community_name', cname)
        const unitNbr = data.UNIT_NBR || data.unitNbr || data.unit_nbr || data.unit || ''
        if (unitNbr) sessionStorage.setItem('unit_nbr', unitNbr)
        const userId = data.USER_ID || data.userId || ''
        if (userId) sessionStorage.setItem('user_id', userId)
        const role = normalizeRole(data.ROLE || data.role)
        const expectedRoute = getPortalRoute(data, { fallback: '/access-denied' })

        // Strict role guard: ManagementPortal is for community admins only.
        if (expectedRoute !== '/management') {
          navigate(expectedRoute)
          return
        }

        if (role) sessionStorage.setItem('user_role', role)
        setIsAdmin(role === 'admin')
        setUser(data)

        // Fetch community feature flags for trial gating
        if (cid) {
          getCommunity({ communityId: cid }).then(comm => {
            if (comm?.FEATURE_FLAGS) setFeatureFlags(comm.FEATURE_FLAGS)
            if (comm?.PLAN) setCommunityPlan((comm.PLAN || '').toUpperCase())
          }).catch(() => {})
        }

        // Register for push notifications (native only, no-op on web)
        if (cid && unitNbr) {
          registerPushNotifications({ communityId: cid, unitNbr, email }).catch(err => {
            console.error('Push registration error:', err)
          })
        } else {
          console.warn('Push: skipped — missing communityId or unitNbr', { cid, unitNbr })
        }
      } catch (err) {
        console.error('registration check failed in ManagementPortal', err)
        sessionStorage.removeItem('id_token')
        navigate('/access-denied')
      }
    })()
  }, [navigate])

  // ---- refresh community feature flags on focus / periodically --------
  useEffect(() => {
    if (!communityId) return
    let mounted = true
    const refreshCommunity = async () => {
      try {
        const comm = await getCommunity({ communityId })
        if (!mounted) return
        if (comm?.FEATURE_FLAGS) setFeatureFlags(comm.FEATURE_FLAGS)
      } catch (err) {
        // ignore transient refresh failures
      }
    }
    window.addEventListener('focus', refreshCommunity)
    const interval = setInterval(refreshCommunity, 15000)
    return () => {
      mounted = false
      window.removeEventListener('focus', refreshCommunity)
      clearInterval(interval)
    }
  }, [communityId])

  // ---- notification store subscription --------------------------------
  useEffect(() => {
    const unsub = subscribeNotifications((list) => setNotifications([...list]));
    const unsubTap = onNotificationTap((notifData) => {
      setNotifPanelOpen(true);
      if (notifData?.data?.type) {
        _handleNotifNavigate({ data: notifData.data });
      }
    });
    return () => { unsub(); unsubTap(); };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ---- navigate to section based on notification type -----------------
  function _handleNotifNavigate(notification) {
    const type = notification?.data?.type;
    setShowMoreMenu(false);
    if (type === 'visitor') {
      setActiveTab('visitors');
    } else if (type === 'entry-exit') {
      setActiveTab('entry-exit');
    } else if (type === 'booking') {
      setActiveTab('clubhouse');
      setClubhouseSubTab('bookings');
    } else if (type === 'announcement') {
      setActiveTab('notifications');
    }
  }

  const [visitors, setVisitors] = useState([]);

  // ---- derived user data -----------------------------------------------
  const userEmail = (() => {
    try {
      const t = sessionStorage.getItem('id_token');
      if (!t) return '';
      const payload = JSON.parse(decodeURIComponent(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
      return (payload.email || '').toString().toLowerCase();
    } catch { return ''; }
  })();
  const userName = (() => {
    const first = (user?.FIRST_NM || user?.first_nm || user?.firstName || '').toString().trim();
    const last = (user?.LAST_NM || user?.last_nm || user?.lastName || '').toString().trim();
    if (first || last) return [first, last].filter(Boolean).join(' ');
    return user?.NAME || user?.name || userEmail.split('@')[0] || 'Manager';
  })();
  const userRole = (user?.ROLE || user?.role || sessionStorage.getItem('user_role') || '').toString().toLowerCase();
  const userUnit = user?.UNIT_NBR || user?.unitNbr || user?.unit_nbr || user?.unit || sessionStorage.getItem('unit_nbr') || '';
  const communityName = user?.COMMUNITY_NAME || user?.communityName || sessionStorage.getItem('community_name') || '';

  async function handleSignOut() {
    let token = sessionStorage.getItem('access_token');
    if (!token) token = sessionStorage.getItem('id_token');
    if (token) {
      try {
        await fetch('https://oauth2.googleapis.com/revoke', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `token=${encodeURIComponent(token)}`
        });
      } catch (err) {
        console.error('Token revoke failed (client-side)', err);
      }
    }
    unregisterPushNotifications().catch(() => {});
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('push_token');
    sessionStorage.removeItem('community_id');
    sessionStorage.removeItem('unit_nbr');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('user_role');
    navigate('/scm/login');
  }

  //Mock data for dashboard
  const dashboardStats = {
    activeVisitors: 12,
    pendingApprovals: 3,
    liftStatus: "operational",
    generatorStatus: "online"
  };

  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false);

  const handleApproveVisitor = (id) => {
  setVisitors((prevVisitors) =>
    prevVisitors.map((v) =>
      v.id === id ? { ...v, status: "approved" } : v
    )
  );
};

const handleAddVisitor = async (newVisitor) => {
  const visitor = {
    id: Date.now().toString(),
    ...newVisitor,
    status: "pending",
  };

  // Optimistically update the UI
  setVisitors((prevVisitors) => [visitor, ...prevVisitors]);

  /*
  // 🔗 Backend API integration (uncomment when backend is ready)
  try {
    const response = await fetch("/api/visitors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(visitor),
    });

    if (!response.ok) {
      throw new Error("Failed to add visitor");
    }

    const savedVisitor = await response.json();
    
    // Optionally update local state with returned visitor (with backend ID)
    setVisitors((prev) =>
      prev.map((v) => (v.id === visitor.id ? savedVisitor : v))
    );
  } catch (error) {
    console.error("Error adding visitor:", error);
    // Optionally rollback the optimistic update
    setVisitors((prev) => prev.filter((v) => v.id !== visitor.id));
    alert("Failed to add visitor. Please try again.");
  }
  */
};


const handleRejectVisitor = (id) => {
  setVisitors((prevVisitors) =>
    prevVisitors.map((v) =>
      v.id === id ? { ...v, status: "rejected" } : v
    )
  );
};

const handleCheckIn = (id) => {
  setVisitors((prevVisitors) =>
    prevVisitors.map((v) =>
      v.id === id
        ? {
            ...v,
            status: "checked-in",
            checkInTime: new Date().toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          }
        : v
    )
  );
};

const handleCheckOut = (id) => {
  setVisitors((prevVisitors) =>
    prevVisitors.map((v) =>
      v.id === id
        ? {
            ...v,
            status: "checked-out",
            checkOutTime: new Date().toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          }
        : v
    )
  );
};



  const handleAddDevice = (device) => {
    // Device management is now handled in SmartDevices component
    setIsAddDeviceOpen(false);
  };

  const handleDeleteDevice = (id) => {
    // Device deletion is now handled in SmartDevices component
    if (window.confirm("Are you sure you want to delete this device?")) {
      // Call API to delete device if needed
    }
  };

  function renderContent() {
    if (!user) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        </div>
      )
    }
    switch (activeTab) {
      case "dashboard":
        return <Dashboard stats={dashboardStats} onNavigate={(tab, filter) => {
          setVisitorFilter(filter);
          setActiveTab(tab);
        }} />;

      case 'visitors': 
        // return ( 
        // <div className="card"> <h3>Visitors</h3> 
        // <p className="muted">Manage expected visitors and approvals.</p> </div> )
        return ( 
                <>
                  {/* <VisitorManagement
                    visitors={visitors}
                    onApprove={handleApproveVisitor}
                    onReject={handleRejectVisitor}
                    onCheckIn={handleCheckIn}
                    onCheckOut={handleCheckOut}
                    onAddVisitor={() => setIsAddVisitorOpen(true)}
                  /> */}
                  <VisitorManagement
                    key={visitorFilter || 'default'}
                    onAddVisitor={() => setIsAddVisitorOpen(true)}
                    initialFilter={visitorFilter}
                    trialMaxPreApproveDay={featureFlags?.max_preapprove_day ?? null}
                    isTrial={isTrial}
                  />
                  {/* Add Visitor Modal */}
                  {/* <AddVisitorModal
                    isOpen={isAddVisitorOpen}
                    onClose={() => setIsAddVisitorOpen(false)}
                    onSubmit={handleAddVisitor}
                  /> */}
                  </>
    );

      case "entry-exit":
        return <EntryExitLog communityId={sessionStorage.getItem('community_id') || ''} trialMaxPreApproveDay={featureFlags?.max_preapprove_day ?? null} isTrial={isTrial} />;

      case "issues":
        return <AdminIssueManagement />;

      case "devices":
        return (
          <>
            <SmartDevices
              onAddDevice={() => setIsAddDeviceOpen(true)}
              onUpdateStatus={() => {}}
              onDeviceSettings={() => {}}
              onDeleteDevice={handleDeleteDevice}
              trialMaxDevices={featureFlags?.max_devices ?? null}
              isTrial={isTrial}
            />

            <AddDeviceModal
              isOpen={isAddDeviceOpen}
              onClose={() => setIsAddDeviceOpen(false)}
              onSubmit={handleAddDevice}
            />
          </>
        );
      case 'notifications':
        return isAdmin ? <BroadcastNotification trialMaxPerMonth={featureFlags?.max_notifications_month ?? null} isTrial={isTrial} /> : null;
      case 'clubhouse': 
      return (
        <div>
          <div className="flex gap-2 px-4 pt-3 pb-1">
            {["bookings", "amenities"].map((t) => (
              <button
                key={t}
                onClick={() => setClubhouseSubTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  clubhouseSubTab === t ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
                }`}
              >
                {t === "bookings" ? "Bookings" : "Manage Amenities"}
              </button>
            ))}
          </div>
          {clubhouseSubTab === "bookings" ? <AdminBookingManagement trialMaxPerMonth={featureFlags?.max_bookings_month ?? null} isTrial={isTrial} /> : <AdminAmenitiesManagement />}
        </div>
      );
      case 'partners': 
      return <PartnersManagement />;
      case 'vacate-requests':
        return isAdmin ? (
          <TrialGate feature="vacate_requests" featureFlags={featureFlags} label="Vacate Requests">
            <AdminVacateManagement />
          </TrialGate>
        ) : null;
      case 'residents': {
        const communityId = sessionStorage.getItem('community_id') || ''
        const idToken = sessionStorage.getItem('id_token') || ''
        return isAdmin ? (
          <div className="space-y-4">
            <ResidentManagement communityId={communityId} idToken={idToken} />
          </div>
        ) : null
      }
      case 'properties': 
      return ( 
      <div className="p-4"> 
      <h2 className="text-lg font-bold">Properties</h2> 
      <p className="text-sm text-gray-500">Rentals, sales and listings.</p> 
      <p className="text-sm font-bold text-red-500">Coming Soon in Beta 2.0</p> 
      </div> 
      );
      default: return null;
    }
  }

  return (
    <div className="min-h-screen portal-bg">
      {/* Header — safe-top pushes below notch / Dynamic Island / status bar */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 sticky top-0 z-50 safe-top safe-left safe-right">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-2">
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">{communityName || 'Smart Community Manager'}</h1>
            <p className="text-xs text-blue-100">{communityName ? 'Powered by Smart Community Manager' : 'Management Portal'}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setNotifPanelOpen(true)}
              className="relative bg-white/20 hover:bg-white/30 active:bg-white/40 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0 text-white"
            >
              <FaBell size={18} className="block" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center font-bold">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <ProfileMenu
              name={userName}
              email={userEmail}
              role={userRole}
              unitNbr={userUnit}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      </div>

      {/* Trial plan banner — shown only when community is on TRIAL plan */}
      <TrialBanner
        communityId={sessionStorage.getItem('community_id') || ''}
        onUpgrade={isAdmin ? async () => {
          const communityId = sessionStorage.getItem('community_id') || ''
          const communityName = sessionStorage.getItem('community_name') || ''
          const idToken = sessionStorage.getItem('id_token') || ''
          await submitUpgradeRequest({ communityId, communityName, feature: 'full_plan', label: 'Full Plan Upgrade' }, idToken)
        } : undefined}
      />

      {/* Content — extra bottom padding for fixed nav + safe area */}
      <div className="max-w-2xl mx-auto p-4" style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}>
        {renderContent()}
      </div>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={notifPanelOpen}
        onClose={() => setNotifPanelOpen(false)}
        notifications={notifications}
        onMarkRead={(id) => markNotificationRead(id)}
        onMarkAllRead={() => markAllNotificationsRead()}
        onClear={() => clearAllNotifications()}
        onNotificationTap={_handleNotifNavigate}
      />

      {/* Bottom Nav — 4 primary tabs + More */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-50 safe-bottom safe-left safe-right">
        <div className="max-w-2xl mx-auto flex justify-around">
          {tabs.slice(0, 4).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setVisitorFilter(null); setShowMoreMenu(false); setActiveTab(tab.id); }}
                className={`flex-1 py-3 flex flex-col items-center text-xs ${
                  isActive ? "text-blue-600 font-semibold" : "text-gray-500"
                }`}
              >
                <Icon size={20} />
                <span className="mt-1 whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
          {/* More button */}
          <button
            onClick={() => setShowMoreMenu((v) => !v)}
            className={`flex-1 py-3 flex flex-col items-center text-xs ${
              showMoreMenu || tabs.slice(4).some((t) => t.id === activeTab)
                ? "text-blue-600 font-semibold"
                : "text-gray-500"
            }`}
          >
            <FaEllipsisH size={20} />
            <span className="mt-1 whitespace-nowrap">More</span>
          </button>
        </div>
      </div>

      {/* More menu overlay */}
      {showMoreMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
          <div className="fixed bottom-[60px] left-0 right-0 z-50 safe-bottom safe-left safe-right flex justify-center">
            <div className="bg-white rounded-t-2xl shadow-lg border max-w-2xl w-full mx-4 p-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {tabs.slice(4).map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => { setVisitorFilter(null); setShowMoreMenu(false); setActiveTab(tab.id); }}
                      className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs ${
                        isActive ? "text-blue-600 font-semibold bg-blue-50" : "text-gray-600"
                      }`}
                    >
                      <Icon size={22} />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
